import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Upload as UploadIcon, X, Check, AlertCircle, ChevronLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const FILE_FIELDS_MULTI = ['partnerIDs', 'proofOfAddress', 'immigrationDocs'];
const FILE_FIELDS_SINGLE = ['legalRepID', 'csf', 'marriageCert'];

const FieldToFormKey = {
  partnerIDs: 'partner_ids',
  legalRepID: 'legal_rep_id',
  proofOfAddress: 'proof_of_address',
  immigrationDocs: 'immigration_docs',
  csf: 'csf',
  marriageCert: 'marriage_cert',
};

// Helper to render an upload box that mirrors the look of the old wizard.
const UploadBox = ({ id, label, help, multiple, files, onPick, onRemove, tr }) => {
  const list = multiple ? files : files ? [files] : [];
  return (
    <div>
      <Label className="text-base font-semibold mb-1 block">{label}</Label>
      {help && <p className="text-sm text-gray-500 mb-2">{help}</p>}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-900 transition-colors">
        <input
          id={id}
          type="file"
          multiple={multiple}
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={(e) => onPick(e.target.files)}
          className="hidden"
          data-testid={`${id}-input`}
        />
        <label
          htmlFor={id}
          className="inline-flex items-center gap-2 cursor-pointer text-sm font-medium text-blue-900 hover:underline"
        >
          <UploadIcon className="w-4 h-4" />
          {multiple
            ? (tr('wizard.uploadCommon.chooseFiles') || 'Choose files')
            : (tr('wizard.uploadCommon.chooseFile') || 'Choose file')}
        </label>
      </div>
      {list.length > 0 && (
        <ul className="mt-2 space-y-1">
          {list.map((f, idx) => (
            <li key={`${f.name}-${idx}`} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded text-sm">
              <span className="truncate">{f.name}</span>
              <button
                type="button"
                onClick={() => onRemove(idx)}
                className="ml-2 text-gray-500 hover:text-red-600"
                aria-label="Remove"
                data-testid={`${id}-remove-${idx}`}
              >
                <X className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const UploadDocuments = () => {
  const { language } = useLanguage();
  const tr = (key) => t(language, key);

  useDocumentMeta({
    title: `${tr('wizard.uploadPage.title')} | BIMCORP MX`,
    description: tr('wizard.uploadPage.subtitle'),
  });

  const [email, setEmail] = useState('');
  const [taxIDs, setTaxIDs] = useState('');
  const [files, setFiles] = useState({
    partnerIDs: [],
    legalRepID: null,
    proofOfAddress: [],
    immigrationDocs: [],
    csf: null,
    marriageCert: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handlePickMulti = (field, picked) => {
    if (!picked) return;
    setFiles((prev) => ({ ...prev, [field]: [...prev[field], ...Array.from(picked)] }));
  };
  const handlePickSingle = (field, picked) => {
    if (!picked) return;
    setFiles((prev) => ({ ...prev, [field]: picked[0] || null }));
  };
  const handleRemoveMulti = (field, idx) => {
    setFiles((prev) => ({ ...prev, [field]: prev[field].filter((_, i) => i !== idx) }));
  };
  const handleRemoveSingle = (field) => {
    setFiles((prev) => ({ ...prev, [field]: null }));
  };

  const totalFiles =
    files.partnerIDs.length +
    (files.legalRepID ? 1 : 0) +
    files.proofOfAddress.length +
    files.immigrationDocs.length +
    (files.csf ? 1 : 0) +
    (files.marriageCert ? 1 : 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
      setError(tr('wizard.common.invalidEmail'));
      return;
    }
    if (totalFiles === 0) {
      setError(tr('wizard.uploadPage.errorNoFiles'));
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('email', email.trim().toLowerCase());
      if (taxIDs.trim()) fd.append('taxIDs', taxIDs.trim());
      FILE_FIELDS_MULTI.forEach((k) => {
        (files[k] || []).forEach((f) => fd.append(FieldToFormKey[k], f));
      });
      FILE_FIELDS_SINGLE.forEach((k) => {
        if (files[k]) fd.append(FieldToFormKey[k], files[k]);
      });

      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/upload-documents`, { method: 'POST', body: fd });

      if (res.status === 404) {
        setError(tr('wizard.uploadPage.errorNotFound'));
        return;
      }
      if (!res.ok) {
        let msg = tr('wizard.uploadPage.errorGeneric');
        try {
          const body = await res.json();
          if (typeof body?.detail === 'string') msg = body.detail;
        } catch (_parse) { /* keep generic */ }
        setError(msg);
        return;
      }

      const body = await res.json();
      setSuccess({
        submissionId: body.submission_id,
        filesUploaded: body.files_uploaded,
      });
    } catch (_err) {
      setError(tr('wizard.uploadPage.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUploadMore = () => {
    setSuccess(null);
    setFiles({
      partnerIDs: [],
      legalRepID: null,
      proofOfAddress: [],
      immigrationDocs: [],
      csf: null,
      marriageCert: null,
    });
    setTaxIDs('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-gray-600 hover:text-blue-900 transition-colors"
            data-testid="upload-back-home"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            {language === 'es' ? 'Inicio' : 'Home'}
          </Link>
        </nav>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">{tr('wizard.uploadPage.title')}</h1>
          <p className="text-base text-gray-600 max-w-xl mx-auto">
            {tr('wizard.uploadPage.subtitle')}
          </p>
        </div>

        <Card className="border-gray-200 shadow-lg">
          <CardContent className="p-6 md:p-8">
            {success ? (
              <div className="text-center py-6" data-testid="upload-success">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 text-green-700 mb-4">
                  <Check className="w-6 h-6" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  {tr('wizard.uploadPage.successTitle')}
                </h2>
                <p className="text-gray-600 mb-6">{tr('wizard.uploadPage.successMessage')}</p>
                <p className="text-xs text-gray-400 mb-6">
                  {language === 'es' ? 'Archivos cargados' : 'Files uploaded'}: {success.filesUploaded}
                </p>
                <Button
                  onClick={handleUploadMore}
                  className="bg-blue-900 hover:bg-blue-800 text-white"
                  data-testid="upload-another-btn"
                >
                  {tr('wizard.uploadPage.uploadAnother')}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-7">
                {/* Email — required */}
                <div>
                  <Label htmlFor="upload-email" className="text-base font-semibold mb-2 block">
                    {tr('wizard.uploadPage.emailLabel')} <span className="text-red-500">*</span>
                  </Label>
                  <p className="text-sm text-gray-500 mb-2">{tr('wizard.uploadPage.emailHelp')}</p>
                  <Input
                    id="upload-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={tr('wizard.uploadPage.emailPlaceholder')}
                    autoComplete="email"
                    required
                    data-testid="upload-email-input"
                  />
                </div>

                <div className="pt-2 border-t border-gray-200" />

                {/* Partner IDs (multi) */}
                <UploadBox
                  id="partnerIDs"
                  label={tr('wizard.uploadPage.partnerIDsLabel')}
                  help={tr('wizard.uploadPage.partnerIDsHelp')}
                  multiple={true}
                  files={files.partnerIDs}
                  onPick={(picked) => handlePickMulti('partnerIDs', picked)}
                  onRemove={(idx) => handleRemoveMulti('partnerIDs', idx)}
                  tr={tr}
                />

                {/* Legal rep ID (single) */}
                <UploadBox
                  id="legalRepID"
                  label={tr('wizard.uploadPage.legalRepIDLabel')}
                  multiple={false}
                  files={files.legalRepID}
                  onPick={(picked) => handlePickSingle('legalRepID', picked)}
                  onRemove={() => handleRemoveSingle('legalRepID')}
                  tr={tr}
                />

                {/* Tax IDs textarea */}
                <div>
                  <Label htmlFor="upload-taxIDs" className="text-base font-semibold mb-2 block">
                    {tr('wizard.uploadPage.taxIDsLabel')}
                  </Label>
                  <p className="text-sm text-gray-500 mb-2">{tr('wizard.uploadPage.taxIDsHelp')}</p>
                  <Textarea
                    id="upload-taxIDs"
                    value={taxIDs}
                    onChange={(e) => setTaxIDs(e.target.value)}
                    placeholder={tr('wizard.uploadPage.taxIDsPlaceholder')}
                    className="min-h-20"
                    data-testid="upload-taxIDs-input"
                  />
                </div>

                <div className="pt-3 mt-4 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {tr('wizard.uploadPage.optionalTitle')}
                  </h3>
                </div>

                <UploadBox
                  id="proofOfAddress"
                  label={tr('wizard.uploadPage.proofOfAddressLabel')}
                  multiple={true}
                  files={files.proofOfAddress}
                  onPick={(picked) => handlePickMulti('proofOfAddress', picked)}
                  onRemove={(idx) => handleRemoveMulti('proofOfAddress', idx)}
                  tr={tr}
                />

                <UploadBox
                  id="immigrationDocs"
                  label={tr('wizard.uploadPage.immigrationDocsLabel')}
                  help={tr('wizard.uploadPage.immigrationDocsHelp')}
                  multiple={true}
                  files={files.immigrationDocs}
                  onPick={(picked) => handlePickMulti('immigrationDocs', picked)}
                  onRemove={(idx) => handleRemoveMulti('immigrationDocs', idx)}
                  tr={tr}
                />

                <UploadBox
                  id="csf"
                  label={tr('wizard.uploadPage.csfLabel')}
                  multiple={false}
                  files={files.csf}
                  onPick={(picked) => handlePickSingle('csf', picked)}
                  onRemove={() => handleRemoveSingle('csf')}
                  tr={tr}
                />

                <UploadBox
                  id="marriageCert"
                  label={tr('wizard.uploadPage.marriageCertLabel')}
                  multiple={false}
                  files={files.marriageCert}
                  onPick={(picked) => handlePickSingle('marriageCert', picked)}
                  onRemove={() => handleRemoveSingle('marriageCert')}
                  tr={tr}
                />

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded flex gap-2"
                       data-testid="upload-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white disabled:opacity-60"
                    data-testid="upload-submit-btn"
                  >
                    {submitting
                      ? tr('wizard.uploadPage.submitting')
                      : tr('wizard.uploadPage.submitBtn')}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UploadDocuments;
